import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { ResponseModel } from '../model/responseModel';
import { TrailerImageModel } from '../model/trailerImageModel';
import { TrailerModel } from '../model/trailerModel';
import { TrailerWithImagesModel } from '../model/trailerWithImagesModel';

import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';
import { Observable } from 'rxjs';
import { FormImageModel } from '../model/formImageModel';


@Injectable()
export class TrailerService {

  protected basePath = 'https://localhost/v1';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(protected httpClient: HttpClient, @Optional() @Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  /**
   * @param consumes string[] mime-types
   * @return true: consumes contains 'multipart/renter-form-data', false: otherwise
   */
  private canConsumeForm(consumes: string[]): boolean {
    const form = 'multipart/renter-form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }


  /**
   *
   * Returns a list of trailers
   */
  public trailerGet(body: any): Observable<ResponseModel<TrailerModel[]>> {
    return this.httpClient.get<ResponseModel<TrailerModel[]>>(`${ this.basePath }/trailer?include=order`,
      {
        params: body
      }
    );
  }

  /**
   *
   * Delete trailer by trailer id
   * @param id Trailer identifier
   */
  public trailerIdDelete(id: number): Observable<ResponseModel<null>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling trailerIdDelete.');
    }

    return this.httpClient.delete<ResponseModel<null>>(`${ this.basePath }/trailer/${ encodeURIComponent(String(id)) }`);
  }

  /**
   *
   * Retrive trailer by trailer id
   * @param id Trailer identifier
   */
  public trailerIdGet(id: number): Observable<ResponseModel<TrailerWithImagesModel>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling trailerIdGet.');
    }

    return this.httpClient.get<ResponseModel<TrailerWithImagesModel>>(`${ this.basePath }/trailer/${ encodeURIComponent(String(id)) }`);
  }

  /**
   *
   * Delete trailer image by trailer id and image id
   * @param id Trailer identifier
   * @param imageId Trailer image identifier
   */
  public trailerIdImageImageIdDelete(id: number, imageId: number): Observable<ResponseModel<null>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling trailerIdImageImageIdDelete.');
    }

    if (imageId === null || imageId === undefined) {
      throw new Error('Required parameter imageId was null or undefined when calling trailerIdImageImageIdDelete.');
    }

    return this.httpClient.delete<ResponseModel<null>>(`${ this.basePath }/trailer/${ encodeURIComponent(String(id)) }/image/${ encodeURIComponent(String(imageId)) }`);
  }

  /**
   *
   * Update trailer image by trailer id and image id. Can be use only for update description and main properties.
   * @param body
   * @param id Trailer identifier
   * @param imageId Trailer image identifier
   */
  public trailerIdImageImageIdPut(body: FormImageModel, id: number, imageId: number): Observable<ResponseModel<TrailerImageModel>> {

    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling trailerIdImageImageIdPut.');
    }

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling trailerIdImageImageIdPut.');
    }

    if (imageId === null || imageId === undefined) {
      throw new Error('Required parameter imageId was null or undefined when calling trailerIdImageImageIdPut.');
    }

    const formParams = new FormData();

    formParams.append(`main[]`, body.main.toString());
    formParams.append(`description[]`, body.description);

    return this.httpClient.put<ResponseModel<TrailerImageModel>>(`${ this.basePath }/trailer/${ encodeURIComponent(String(id)) }/image/${ encodeURIComponent(String(imageId)) }`,
      body
    );
  }

  /**
   *
   * Creates a new trailer
   * @param id Trailer identifier
   * @param images
   */
  public trailerIdImagePost(id: number, image: FormImageModel): Observable<ResponseModel<TrailerImageModel>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling trailerIdImagePost.');
    }

    const formParams = new FormData();

    formParams.append(`images[]`, image.image);
    formParams.append(`main[]`, image.main.toString());
    formParams.append(`description[]`, image.description);

    return this.httpClient.post<ResponseModel<TrailerImageModel>>(`${ this.basePath }/trailer/${ encodeURIComponent(String(id)) }/image`,
      formParams
    );
  }

  /**
   *
   * Update trailer by station id
   * @param body
   * @param id Trailer identifier=\
   */
  public trailerIdPut(body: TrailerModel, id: number): Observable<ResponseModel<TrailerModel>> {

    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling trailerIdPut.');
    }

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling trailerIdPut.');
    }

    return this.httpClient.put<ResponseModel<TrailerModel>>(`${ this.basePath }/trailer/${ encodeURIComponent(String(id)) }`,
      body
    );
  }

  /**
   *
   * Creates a new trailer
   * @param body
   */
  public trailerPost(body: TrailerModel): Observable<ResponseModel<TrailerModel>> {

    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling trailerPost.');
    }

    return this.httpClient.post<ResponseModel<TrailerModel>>(`${ this.basePath }/trailer`,
      body
    );
  }

}
